import { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import PersonIcon from '@mui/icons-material/Person'
import LocalDrinkIcon from '@mui/icons-material/LocalDrink'
import FastfoodIcon from '@mui/icons-material/Fastfood'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import WcOutlinedIcon from '@mui/icons-material/WcOutlined'
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined'
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined'
import LocalCafeIcon from '@mui/icons-material/LocalCafe'
import Tooltip from '@mui/material/Tooltip'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'

// Helper function to get service icons
const getServiceIcons = (location, t) => {
  const icons = []

  // Add crew support icon if applicable
  if (location.crewSupport === 'YES') {
    icons.push({ icon: <PersonIcon fontSize='small' />, tooltip: t('courseMapDrawer.crewSupport'), service: 'crewSupport' })
  }

  // Add service icons based on services string
  if (location.services) {
    const services = location.services.toLowerCase()

    if (services.includes('drinks') || services.includes('drink')) {
      icons.push({ icon: <LocalDrinkIcon fontSize='small' />, tooltip: t('courseMapDrawer.drinks'), service: 'drinks' })
    }

    if (services.includes('snacks') || services.includes('snack')) {
      icons.push({ icon: <FastfoodIcon fontSize='small' />, tooltip: t('courseMapDrawer.snacks'), service: 'snacks' })
    }

    if (services.includes('hot food') || services.includes('restaurant')) {
      icons.push({ icon: <RestaurantIcon fontSize='small' />, tooltip: t('courseMapDrawer.hotFood'), service: 'hot food' })
    }

    if (services.includes('toilet')) {
      icons.push({ icon: <WcOutlinedIcon fontSize='small' />, tooltip: t('courseMapDrawer.toilet'), service: 'toilet' })
    }

    if (services.includes('drop bag')) {
      icons.push({ icon: <LocalMallOutlinedIcon sx={{ color: '#a6ff00' }} fontSize='small' />, tooltip: t('courseMapDrawer.dropBag'), service: 'drop bag' })
    }

    if (services.includes('main checkpoint')) {
      icons.push({ icon: <LocalPoliceOutlinedIcon fontSize='small' sx={{ color: '#ff00f2' }} />, tooltip: t('courseMapDrawer.mainCheckpoint'), service: 'main checkpoint' })
    }

    if (services.includes('cafe') || services.includes('café')) {
      icons.push({ icon: <LocalCafeIcon fontSize='small' />, tooltip: t('courseMapDrawer.cafe'), service: 'cafe' })
    }
  }

  return icons
}

// Legend data for service icons
const serviceLegend = [
  { icon: <PersonIcon fontSize='small' />, labelKey: 'courseMapDrawer.crewSupport', service: 'crewSupport' },
  { icon: <LocalDrinkIcon fontSize='small' />, labelKey: 'courseMapDrawer.drinks', service: 'drinks' },
  { icon: <FastfoodIcon fontSize='small' />, labelKey: 'courseMapDrawer.snacks', service: 'snacks' },
  { icon: <RestaurantIcon fontSize='small' />, labelKey: 'courseMapDrawer.hotFood', service: 'hot food' },
  { icon: <WcOutlinedIcon fontSize='small' />, labelKey: 'courseMapDrawer.toilet', service: 'toilet' },
  { icon: <LocalMallOutlinedIcon fontSize='small' sx={{ color: '#a6ff00' }} />, labelKey: 'courseMapDrawer.dropBag', service: 'drop bag' },
  { icon: <LocalPoliceOutlinedIcon fontSize='small' sx={{ color: '#ff00f2' }} />, labelKey: 'courseMapDrawer.mainCheckpoint', service: 'main checkpoint' },
  { icon: <LocalCafeIcon fontSize='small' />, labelKey: 'courseMapDrawer.cafe', service: 'cafe' }
]

// Test cases to verify the service icon mapping
// const testLocation1 = {
//   crewSupport: 'YES',
//   services: 'Drinks + Snacks + Toilet + Drop Bag'
// }
//
// const testLocation2 = {
//   crewSupport: 'NO',
//   services: 'Main Checkpoint Hot Food + Toilet'
// }
//
// console.log('Test 1 - Crew Support + Multiple Services:', getServiceIcons(testLocation1))
// console.log('Test 2 - No Crew Support + Hot Food:', getServiceIcons(testLocation2))

export default function CourseMapDrawer ({
  drawerOpen,
  setDrawerOpen,
  showDistanceMarkers,
  setShowDistanceMarkers,
  distanceInterval,
  setDistanceInterval,
  showNames,
  setShowNames,
  locations,
  handleLocationClick,
  formatLocationLabel,
  showUserLocation,
  setShowUserLocation,
  locationPermissionGranted
}) {
  const { t } = useTranslation('raceGuide')
  const [searchValue, setSearchValue] = useState('')
  const [selectedService, setSelectedService] = useState('all')
  const [mapOptionsExpanded, setMapOptionsExpanded] = useState(false)

  // Filter locations based on search and service filter
  const filteredLocations = useMemo(() => {
    if (!locations) return []

    return locations.filter(location => {
      // Search filter (case insensitive)
      const matchesSearch = searchValue === '' ||
        formatLocationLabel(location).toLowerCase().includes(searchValue.toLowerCase())

      // Service filter
      if (selectedService === 'all') {
        return matchesSearch
      }

      // Special case for crew support
      if (selectedService === 'crewSupport') {
        const hasCrewSupport = location.crewSupport === 'YES'
        return matchesSearch && hasCrewSupport
      }

      const serviceIcons = getServiceIcons(location, t)
      const hasService = serviceIcons.some(icon =>
        icon.service === selectedService
      )

      return matchesSearch && hasService
    })
  }, [locations, searchValue, selectedService, formatLocationLabel, t])

  return (
    <Drawer
      anchor='bottom'
      open={drawerOpen}
      slotProps={{
        backdrop: {
          invisible: true,
        },
      }}
      onClose={() => setDrawerOpen(false)}
      sx={{
        zIndex: 2600,
        '.MuiDrawer-paper': {
          height: 'auto',
          maxHeight: '83vh',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }
      }}
    >
      <Box sx={{ p: 2, width: '100vw' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setMapOptionsExpanded(!mapOptionsExpanded)}>
              <Typography variant='h6' gutterBottom sx={{ flexGrow: 1 }}>
                {t('courseMapDrawer.mapOptions')}
              </Typography>
              {mapOptionsExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </Box>
            {mapOptionsExpanded && (
              <List dense>
                <ListItem>
                  <ListItemText primary={t('courseMapDrawer.showDistanceMarkers')} />
                  <Switch
                    edge='end'
                    checked={showDistanceMarkers}
                    onChange={() => setShowDistanceMarkers(!showDistanceMarkers)}
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary={t('courseMapDrawer.distanceInterval', { interval: distanceInterval })}
                    secondary={distanceInterval === 1 ? t('courseMapDrawer.showEvery1') : t('courseMapDrawer.showEvery5')}
                  />
                  <Switch
                    edge='end'
                    checked={distanceInterval === 5}
                    onChange={() => setDistanceInterval(distanceInterval === 1 ? 5 : 1)}
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary={t('courseMapDrawer.toggleShowNames')}
                    secondary={showNames ? t('courseMapDrawer.showNames') : t('courseMapDrawer.hideNames')}
                  />
                  <Switch
                    edge='end'
                    checked={showNames}
                    onChange={() => setShowNames(!showNames)}
                  />
                </ListItem>

                {locationPermissionGranted && (
                  <ListItem>
                    <ListItemText
                      primary={t('courseMapDrawer.showMyLocation')}
                      secondary={showUserLocation ? t('courseMapDrawer.locationVisible') : t('courseMapDrawer.locationHidden')}
                    />
                    <Switch
                      edge='end'
                      checked={showUserLocation}
                      onChange={() => setShowUserLocation(!showUserLocation)}
                      disabled={!locationPermissionGranted}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
            {/* Search and Filter Controls */}
            <Typography variant='h6' gutterBottom>
              {t('courseMapDrawer.waypoints', { shown: filteredLocations?.length || 0, total: locations?.length || 0 })}
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Autocomplete
                freeSolo
                options={locations?.map(location => formatLocationLabel(location)) || []}
                value={searchValue}
                onChange={(event, newValue) => setSearchValue(newValue || '')}
                onInputChange={(event, newInputValue) => setSearchValue(newInputValue)}
                renderInput={(params) => (
                  <TextField variant='filled' {...params} label={t('courseMapDrawer.searchLocations')} size='small' sx={{ backgroundColor: '#333', maxWidth: 260, borderRadius: 1 }} />
                )}
                sx={{ flexGrow: 1 }}
              />
              <FormControl size='small' sx={{ minWidth: 150 }}>
                <InputLabel sx={{ mt: -0.5 }}>{t('courseMapDrawer.filterByService')}</InputLabel>
                <Select
                  native
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  label={t('courseMapDrawer.filterByService')}
                >
                  <option value='all'>{t('courseMapDrawer.allServices')}</option>
                  <option value='crewSupport'>{t('courseMapDrawer.crewSupport')}</option>
                  <option value='drinks'>{t('courseMapDrawer.drinks')}</option>
                  <option value='snacks'>{t('courseMapDrawer.snacks')}</option>
                  <option value='hot food'>{t('courseMapDrawer.hotFood')}</option>
                  <option value='toilet'>{t('courseMapDrawer.toilet')}</option>
                  <option value='drop bag'>{t('courseMapDrawer.dropBag')}</option>
                  <option value='main checkpoint'>{t('courseMapDrawer.mainCheckpoint')}</option>
                  <option value='cafe'>{t('courseMapDrawer.cafe')}</option>
                </Select>
              </FormControl>
            </Box>

            <List dense sx={{ overflow: 'scroll', height: '25vh' }}>
              {/* Split into two columns when more than 8 items and at least 2 items in second column */}
              {filteredLocations?.map((location, index) => {
                  const serviceIcons = getServiceIcons(location, t)
                  return (
                    <ListItem
                      dense
                      key={`location-${index}`}
                      onClick={() => handleLocationClick(location)}
                      sx={{
                        cursor: 'pointer',
                        pl: 4,
                        maxWidth: 420,
                        backgroundColor: index % 2 === 0 ? '#222' : 'transparent'
                      }}
                    >
                      <ListItemText primary={formatLocationLabel(location)} />
                      {serviceIcons.length > 0 && (
                        <Box sx={{ display: 'flex', mr: 1 }}>
                          {serviceIcons.map((serviceIcon, iconIndex) => (
                            <Tooltip key={`icon-${iconIndex}`} title={serviceIcon.tooltip}>
                              <span style={{ marginRight: '4px' }}>
                                {serviceIcon.icon}
                              </span>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </ListItem>
                  )
                })}
            </List>
          </Grid>
        </Grid>

        {/* Legend Section */}
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #333' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant='h6' gutterBottom sx={{ mt: 0.4 }}>
              {t('courseMapDrawer.legend')}
            </Typography>

            {serviceLegend.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {item.icon}
                <Typography variant='caption'>{t(item.labelKey)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}